/**
 * User Favorites Route - React Router v7
 *
 * Route: /user/favorites/:section?
 * Description: User favorite ads and dealers
 * Sections: ads (default) | dealers
 */
/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-throw-literal, @typescript-eslint/strict-boolean-expressions, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unused-vars */
import type {LoaderFunctionArgs} from 'react-router-dom'
import {useLoaderData} from 'react-router-dom'

import cookie from '@s-ui/js/lib/cookie'

import UserFavorites from './index.js'

// Import styles
import './styles/index.scss'

const PAGINATION_PLACEHOLDER = '%{pageNumber}'
const DEFAULT_PAGINATION_PLACEHOLDER_URL = '#'
const IS_FAVORITE_LIST = true

/**
 * Mark ads/dealers as favorited
 */
const markAsFaved = (items: any[]) =>
  items.map(item => ({
    ...item,
    isFavorite: IS_FAVORITE_LIST
  }))

/**
 * Loader for User Favorites page
 * Fetches favorite ads, dealers, SEO data, and tracking data
 */
export async function loader({context, request, params}: LoaderFunctionArgs) {
  const {domain, i18n, cookies} = context
  const {culture} = i18n
  const url = new URL(request.url)
  const path = url.pathname
  const query = Object.fromEntries(url.searchParams)
  const queryParams = url.search
  const {section} = params

  const {unsubscribeFavorite} = cookie.parse(cookies)

  try {
    // Step 1: Get page name, pagination, and search data in parallel
    const [pageNameResult, paginationResult, searchDataResult] = await Promise.allSettled([
      domain.get('get_page_name_page_use_case').execute({path}),
      domain.get('get_search_pagination_url_ads_use_case').execute({
        paginationPlaceholder: PAGINATION_PLACEHOLDER,
        queryParams: query,
        url: path
      }),
      domain.get('get_search_data_from_url_search_use_case').execute({
        path,
        query,
        url: path + queryParams
      })
    ])

    const pageName = pageNameResult.status === 'fulfilled' ? pageNameResult.value.pageName : 'user_favorites'

    const paginationUrl =
      paginationResult.status === 'fulfilled' && paginationResult.value?.[1]
        ? paginationResult.value[1]
        : DEFAULT_PAGINATION_PLACEHOLDER_URL

    const searchDataError = searchDataResult.status === 'rejected'
    const searchDataResponse =
      searchDataResult.status === 'fulfilled' && !searchDataResult.value?.[0] ? searchDataResult.value[1] : null

    // Extract search object - default to empty object if undefined to avoid component errors
    const search = searchDataResponse?.toFiltersSearch?.()?.search || {}

    // Step 2: Get favorites
    let ads: any[] = []
    let totalAds = 0
    let dealers: any[] = []
    let totalDealers = 0

    try {
      const favoritesData = await domain.get('get_user_favorites_use_case').execute({search})
      ads = markAsFaved(favoritesData?.ads || [])
      totalAds = favoritesData?.totalAds || 0
      dealers = markAsFaved(favoritesData?.contracts || [])
      totalDealers = favoritesData?.totalContracts || 0
    } catch (error) {
      console.error('[UserFavorites] Error fetching favorites:', error)
    }

    // Step 3: Get SEO data and tracking data in parallel
    const [seoDataResult, trackingDataResult] = await Promise.allSettled([
      domain.get('get_seo_data_use_case').execute({culture, pageName}),
      domain.get('get_page_tracking_data_use_case').execute(
        section === 'dealers'
          ? {
              products: dealers,
              page: pageName
            }
          : {
              advertisements: ads,
              page: pageName,
              search
            }
      )
    ])

    // Extract helmet from SEO data - API returns [error, {head: helmet}]
    const helmet =
      seoDataResult.status === 'fulfilled' && seoDataResult.value?.[1]?.head ? seoDataResult.value[1].head : {}
    const pageTrackingData = trackingDataResult.status === 'fulfilled' ? trackingDataResult.value || {} : {}

    return {
      ads,
      dealers,
      totalAds,
      totalDealers,
      pageName,
      helmet,
      paginationPlaceholderUrl: paginationUrl,
      showSearchSort: !searchDataError,
      search,
      pageTrackingData,
      unsubscribeFavorite,
      section: section || 'ads'
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[UserFavorites] Loader error:', error)
    return {
      ads: [],
      dealers: [],
      totalAds: 0,
      totalDealers: 0,
      pageName: 'user_favorites',
      helmet: {},
      paginationPlaceholderUrl: DEFAULT_PAGINATION_PLACEHOLDER_URL,
      showSearchSort: false,
      search: {},
      pageTrackingData: {},
      unsubscribeFavorite: null,
      section: section || 'ads'
    }
  }
}

/**
 * Component wrapper
 * Uses existing UserFavorites component
 */
export function Component() {
  const data = useLoaderData()
  return <UserFavorites {...data} />
}

export default {loader, Component}
